/**
 * Cross-Thread Search Global Tool
 *
 * (PR fix/thread-isolation-regression-and-cross-thread-retrieval)
 *
 * Miles directive (2026-05-09):
 *
 *   "Each thread needs to be separate in conversation history to keep
 *    projects separate but Prest0n can pull info from different current
 *    or old archived threads of course like you do in this Slack thread."
 *
 * This tool surfaces relevant snippets from OTHER current and archived
 * threads when the user references something they discussed elsewhere
 * (e.g. "what was the answer on the workers' comp question I asked
 * last week?"). It calls the pure `crossThreadSearch` service, formats
 * up to 5 top results into an inline citation block, and returns
 * control to the orchestrator/conversation flow.
 *
 * Triggers:
 *   \u2022 Phrases like "the [topic] thread", "last week I asked",
 *     "in the previous chat", "remember when", "el hilo de", etc.
 *   \u2022 Explicit "search across threads", "search archived chats".
 *
 * The literal name `crossThreadSearch` is the live-bundle verification
 * grep marker for this fix.
 */

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  TriggerMatch,
  ToolTheme,
} from '../types';
import {
  crossThreadSearch,
  detectsCrossThreadIntent,
  type CrossThreadSearchResult,
} from '../../crossThreadSearch';
import { getAuth } from 'firebase/auth';
import { hasNearbyNegation } from '../triggerHelpers';

// ────────────────────────────────────────────────────────────────────────────
// Theme + state
// ────────────────────────────────────────────────────────────────────────────

const CROSS_THREAD_THEME: ToolTheme = {
  accent: '#1f2937',     // slate-800
  accentBg: '#f3f4f6',   // gray-100
  border: 'var(--brand-gold)',     // Prest0 gold
  subtitle: '#475569',   // slate-600
  muted: '#94a3b8',      // slate-400
};

interface CrossThreadSearchState {
  query: string;
  archivedOnly: boolean;
  results: CrossThreadSearchResult[];
  status: 'idle' | 'searching' | 'complete' | 'no-results' | 'error';
}

// ────────────────────────────────────────────────────────────────────────────
// Trigger
// ────────────────────────────────────────────────────────────────────────────

/**
 * The orchestrator will normally invoke this tool by name; the trigger
 * pattern below is a defensive fallback for direct natural-language
 * activation. We accept the canonical English/Spanish cross-thread
 * intent phrases plus a few explicit verbs.
 */
// Phase 0 hardening: explicit patterns anchored to the start; the fuzzy
// `detectsCrossThreadIntent` heuristic is gated by a negation guard
// against the cross-thread anchor keywords so phrases like
// "I'm not asking about other threads" don't fire it.
const CROSS_THREAD_EXPLICIT_PATTERNS: RegExp[] = [
  /^search (?:across )?(?:my )?(?:other )?threads?\s*[:\-]?\s*(.+)/i,
  /^search archived (?:threads?|chats?)\s*[:\-]?\s*(.+)/i,
  /^find (?:in|across) (?:my )?(?:other )?threads?\s*[:\-]?\s*(.+)/i,
  /^buscar (?:en )?(?:los )?(?:otros )?hilos\s*[:\-]?\s*(.+)/i,
];

const CROSS_THREAD_NEGATION_ANCHOR = /\b(threads?|chats?|hilos?|conversations?)\b/i;

function matchCrossThreadTrigger(text: string): TriggerMatch {
  const lower = (text || '').toLowerCase().trim();
  if (!lower) return { matches: false };

  for (const re of CROSS_THREAD_EXPLICIT_PATTERNS) {
    const m = lower.match(re);
    if (!m || typeof m.index !== 'number') continue;
    if (hasNearbyNegation(lower, m.index, m[0].length)) continue;
    return { matches: true, query: m[1].trim() };
  }

  // Permissive fallback — use the heuristic from the service module,
  // gated by a negation guard against the cross-thread anchor keyword.
  if (detectsCrossThreadIntent(lower)) {
    const anchor = lower.match(CROSS_THREAD_NEGATION_ANCHOR);
    if (anchor && typeof anchor.index === 'number' &&
        hasNearbyNegation(lower, anchor.index, anchor[0].length)) {
      return { matches: false };
    }
    return { matches: true, query: lower };
  }

  return { matches: false };
}

// ────────────────────────────────────────────────────────────────────────────
// Rendering helpers
// ────────────────────────────────────────────────────────────────────────────

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function formatTimestamp(ts: string | undefined): string {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10); // YYYY-MM-DD
  } catch {
    return '';
  }
}

/**
 * Render the search results as a markdown block the LLM/user can read.
 * Each result links to its source thread via `#thread-<id>` so the chat
 * UI can intercept the click and switch threads.
 */
function renderResultsMarkdown(query: string, results: CrossThreadSearchResult[]): string {
  if (results.length === 0) {
    return `_No matches found in your other threads for_ \`${query}\`.`;
  }
  const lines: string[] = [];
  lines.push(`**Found ${results.length} relevant snippet${results.length === 1 ? '' : 's'} from your other threads:**`);
  lines.push('');
  for (let i = 0; i < results.length; i++) {
    const r = results[i];
    const tag = r.archived ? ' (archived)' : '';
    const date = formatTimestamp(r.timestamp);
    const dateLabel = date ? ` \u2014 ${date}` : '';
    const role = r.isUser ? 'you' : 'Prest0n';
    lines.push(`${i + 1}. [Thread: ${r.threadName}](#thread-${r.threadId})${tag}${dateLabel}`);
    lines.push(`   _${role}_: ${r.snippet}`);
    lines.push('');
  }
  return lines.join('\n');
}

function renderResultsHtml(query: string, results: CrossThreadSearchResult[]): string {
  const safeQuery = escapeHtml(query);
  if (results.length === 0) {
    return `<div style="padding:14px;border:1px solid #e5e7eb;border-radius:10px;background:#f9fafb;color:#475569;font-size:0.9rem;">No matches found in your other threads for <strong>${safeQuery}</strong>.</div>`;
  }
  const items = results.map((r, i) => {
    const tag = r.archived
      ? ' <span style="font-size:0.72rem;color:#94a3b8;background:#f1f5f9;padding:2px 6px;border-radius:6px;margin-left:6px;">archived</span>'
      : '';
    const date = formatTimestamp(r.timestamp);
    const dateLabel = date
      ? `<span style="font-size:0.75rem;color:#64748b;margin-left:8px;">${date}</span>`
      : '';
    const role = r.isUser ? 'you' : 'Prest0n';
    return `
      <li style="margin-bottom:14px;list-style:none;padding-left:0;">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-bottom:4px;">
          <span style="font-weight:600;color:#475569;font-size:0.78rem;">${i + 1}.</span>
          <a href="#thread-${escapeHtml(r.threadId)}" data-thread-id="${escapeHtml(r.threadId)}"
             style="color:#0f172a;font-weight:600;font-size:0.92rem;text-decoration:underline;text-underline-offset:2px;">
            ${escapeHtml(r.threadName || 'Untitled thread')}
          </a>
          ${tag}${dateLabel}
        </div>
        <div style="font-size:0.86rem;color:#334155;line-height:1.45;padding-left:18px;">
          <span style="color:#94a3b8;font-style:italic;margin-right:4px;">${role}:</span>
          ${escapeHtml(r.snippet)}
        </div>
      </li>
    `;
  }).join('');
  return `
    <div style="padding:14px;border:1px solid #e5e7eb;border-radius:10px;background:#f9fafb;font-family:Inter,-apple-system,BlinkMacSystemFont,sans-serif;">
      <div style="font-size:0.82rem;color:#64748b;margin-bottom:10px;">
        Found <strong style="color:#0f172a;">${results.length}</strong>
        relevant snippet${results.length === 1 ? '' : 's'}
        from your other threads for <strong style="color:#0f172a;">${safeQuery}</strong>.
      </div>
      <ul style="margin:0;padding:0;">${items}</ul>
    </div>
  `;
}

// ────────────────────────────────────────────────────────────────────────────
// Tool definition
// ────────────────────────────────────────────────────────────────────────────

export const crossThreadSearchTool: GlobalToolDefinition<CrossThreadSearchState> = {
  id: 'cross-thread-search',
  name: 'Cross-Thread Search',
  description:
    'Pull a relevant snippet from another current or archived thread. Use when the user references something they discussed in a different chat, e.g. "the workers\' comp thread", "last week I asked", "remember when".',

  theme: CROSS_THREAD_THEME,
  buttonIds: {} as Record<string, string>,

  triggerPattern: matchCrossThreadTrigger,

  createInitialState: (): CrossThreadSearchState => ({
    query: '',
    archivedOnly: false,
    results: [],
    status: 'idle',
  }),

  steps: [
    {
      id: 'search',
      name: 'Search Threads',
      activeDisplayName: 'searching your other threads',

      execute: async (
        ctx: StepContext<CrossThreadSearchState>,
        rawInput?: unknown,
      ): Promise<StepResult> => {
        const input = (rawInput as
          | { query?: string; archivedOnly?: boolean; threadFilter?: string[] }
          | undefined) || {};

        const query = (input.query || '').trim();
        const archivedOnly = !!input.archivedOnly;
        const threadFilter = Array.isArray(input.threadFilter) ? input.threadFilter : undefined;

        if (!query) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'no query provided' },
            sections: [
              {
                type: 'text',
                content:
                  "Tell me what you're looking for from your other threads \u2014 for example, \"the workers' comp question I asked last week\".",
              },
            ],
            theme: 'blue',
          });
          return { status: 'completed', nextStep: null };
        }

        ctx.setState({ query, archivedOnly, status: 'searching' });
        const handle = ctx.messaging.showPending('searching your threads');

        // Pull instanceId from sharedContext. When the host doesn't set it
        // (single-thread mode, internal pages with the switcher disabled),
        // there's literally nothing to search \u2014 short-circuit politely.
        const instanceId = (ctx.sharedContext as { chatInstanceId?: string | null }).chatInstanceId || '';
        if (!instanceId) {
          handle.finalize({
            text: 'cross-thread search unavailable on this surface',
            html: `<div style="padding:14px;color:#475569;font-size:0.9rem;">Cross-thread search isn\u2019t available on this chat surface (no thread switcher is active).</div>`,
            sourceTag: 'cross-thread-search-unavailable',
          });
          ctx.setState({ status: 'no-results' });
          return { status: 'completed', nextStep: null };
        }

        try {
          // Active thread id, if available, is excluded so we don't echo
          // the user's current thread back at them. We don't have direct
          // access to the active thread in this tool context; the chat
          // host could set it on sharedContext later. For now we don't
          // exclude \u2014 the caller can pass `threadFilter` if they want
          // to be picky.
          const results = crossThreadSearch({
            instanceId,
            uid: getAuth().currentUser?.uid || null,
            query,
            archivedOnly,
            threadFilter,
            maxResults: 5,
            maxSnippetChars: 240,
          });

          ctx.setState({ results, status: results.length > 0 ? 'complete' : 'no-results' });

          handle.finalize({
            text: renderResultsMarkdown(query, results),
            html: renderResultsHtml(query, results),
            sourceTag: results.length > 0 ? 'cross-thread-search-hits' : 'cross-thread-search-empty',
          });

          return { status: 'completed', nextStep: null };
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          ctx.setState({ status: 'error' });
          handle.finalize({
            text: `cross-thread search failed: ${message}`,
            html: `<div style="padding:14px;color:#dc2626;font-size:0.9rem;">Cross-thread search hit an error: ${escapeHtml(message)}</div>`,
            sourceTag: 'cross-thread-search-error',
          });
          return { status: 'failed', error: err instanceof Error ? err : new Error(message) };
        }
      },
    },
  ],
};

export default crossThreadSearchTool;
